import {announce} from '@github-ui/aria-live'
import type {CodeNavigationInfo, DefinitionOrReference} from '@github-ui/code-nav'
import {usePath} from '@github-ui/code-view-shared/contexts/PathContext'
import {noop} from '@github-ui/noop'
import {useTaillightsController} from '@github-ui/taillights/provider'
import type {SourceDocument} from '@github-ui/taillights-worker/document'
import {useInFileSearchResults} from '@github-ui/use-in-file-search-results'
import {useTheme} from '@primer/react'
import {type ReactNode, use, useEffect, useMemo, useState} from 'react'

import {useCurrentStyledBlob} from '../../hooks/CurrentStyledBlob'
import {CodeBlobTaillightsProvider} from './CodeBlobTaillightsProvider'
import {TaillightsBlobContext} from './source-context'

// Stable empty reference so the derived symbol highlight keeps an identical identity when nothing is selected.
const EMPTY_SEARCH_RESULTS: DefinitionOrReference[] = []
const EMPTY_SOURCE_LINES: string[] = []

function useWorkerMatches(codeNavInfo: CodeNavigationInfo | undefined, query: string) {
  const controller = useTaillightsController()
  const request = useMemo(() => ({controller, codeNavInfo, query}), [controller, codeNavInfo, query])
  const [response, setResponse] = useState<{request: typeof request; results: DefinitionOrReference[]}>()
  const validQuery = query.length > 0 && query.length <= 1000
  useEffect(() => {
    if (!controller || !codeNavInfo || !validQuery) return
    let active = true
    void (async () => {
      try {
        const result = await controller.search({query, kind: 'symbol', index: 0})
        if (active) setResponse({request, results: codeNavInfo.createReferences([...result.highlights])})
      } catch (error) {
        if (!active || controller.disposed) return
        reportError(error)
        announce('Symbol highlighting failed. Select the symbol again to retry.')
        setResponse({request, results: []})
      }
    })()
    return () => {
      active = false
    }
  }, [controller, codeNavInfo, query, validQuery, request])
  return validQuery && response?.request === request ? response.results : EMPTY_SEARCH_RESULTS
}

type BlobSearchState = Pick<
  ReturnType<typeof useInFileSearchResults>,
  'searchResults' | 'focusedSearchResult' | 'setFocusedSearchResult' | 'searchStatus'
> & {
  overlaySearchResults: DefinitionOrReference[]
  overlayFocusedSearchResult: number | undefined
}

type SearchProps = {
  codeNavInfo: CodeNavigationInfo | undefined
  searchTerm: string
  selectedSymbol: string
  findInFileActive: boolean
  children: (search: BlobSearchState) => ReactNode
}

function BlobSearch({codeNavInfo, searchTerm, selectedSymbol, findInFileActive, children}: SearchProps) {
  // Find-in-file matches come from the worker, keyed on the active search term.
  const search = useInFileSearchResults(codeNavInfo, searchTerm)

  // The clicked-symbol highlight is a pure function of the current blob and the selected symbol, so we derive
  // it during render instead of pushing it up from the panel through an effect. `getReferencesToSymbol` returns
  // every in-file occurrence (definition + usages) — exactly the set the overlay highlights — synchronously, so
  // the clicked token is highlighted immediately and reliably regardless of how async code-nav defs/refs resolve.
  const symbol = useMemo(
    () => (codeNavInfo && selectedSymbol ? codeNavInfo.getReferencesToSymbol(selectedSymbol) : EMPTY_SEARCH_RESULTS),
    [codeNavInfo, selectedSymbol],
  )
  return children({
    ...search,
    // Find-in-file owns the highlight overlay while it is actively searching; otherwise the selected symbol does.
    overlaySearchResults: findInFileActive ? search.searchResults : symbol,
    overlayFocusedSearchResult: findInFileActive ? search.focusedSearchResult : undefined,
  })
}

function TaillightsBlobSearch({
  codeNavInfo,
  selectedSymbol,
  children,
}: Pick<SearchProps, 'codeNavInfo' | 'selectedSymbol' | 'children'>) {
  const symbol = useWorkerMatches(codeNavInfo, selectedSymbol)
  return children({
    searchResults: EMPTY_SEARCH_RESULTS,
    focusedSearchResult: undefined,
    setFocusedSearchResult: noop,
    searchStatus: 'done',
    overlaySearchResults: symbol,
    overlayFocusedSearchResult: undefined,
  })
}

export function BlobSearchProvider({
  taillights,
  tabSize,
  ...props
}: SearchProps & {taillights: boolean; tabSize: number}) {
  if (!taillights) return <BlobSearch {...props} />
  return <TaillightsBlobSearchProvider {...props} tabSize={tabSize} />
}

function TaillightsBlobSearchProvider({tabSize, ...props}: SearchProps & {tabSize: number}) {
  const path = usePath()
  const {rawLines, stylingDirectives} = useCurrentStyledBlob()
  const {colorScheme} = useTheme()
  const {codeNavInfo} = props
  const source = useMemo<SourceDocument>(
    () => ({
      id: path,
      lines: rawLines ?? EMPTY_SOURCE_LINES,
      directives: stylingDirectives,
      sections: codeNavInfo ? [...new Set([...codeNavInfo.codeSections.values()].flat())] : [],
    }),
    [path, rawLines, stylingDirectives, codeNavInfo],
  )
  return (
    <TaillightsBlobContext value>
      <CodeBlobTaillightsProvider source={source} tabSize={tabSize} appearanceKey={colorScheme}>
        <TaillightsBlobSearch codeNavInfo={props.codeNavInfo} selectedSymbol={props.selectedSymbol}>
          {props.children}
        </TaillightsBlobSearch>
      </CodeBlobTaillightsProvider>
    </TaillightsBlobContext>
  )
}

export function useTaillightsBlobEnabled() {
  return use(TaillightsBlobContext)
}
